import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "@/constants/api";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { isAbortError } from "@/hooks/useAbortableLoader";
import { REPORT_FIELD_LABELS } from "@/reports/shared_utils/reportFieldLabels";
import { ReportDefaultsConfirmModal } from "@/components/modals/ReportDefaultsConfirmModal";
import { getPageComponent } from "@/reports/registry";
import { getFolderLogos } from "@/utils/imageCache";

export const ReportDefaults = () => {
  const { reportFolderId } = useParams();
  const { getToken, userId } = useAuth();

  const [reportType, setReportType] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [existingDefaults, setExistingDefaults] = useState({});
  const [logos, setLogos] = useState([]);
  const [logosVersion, setLogosVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [valuesToSave, setValuesToSave] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    const ac = new AbortController();
    const fetchReportFolder = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/report-folders/${reportFolderId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch Report Folder");
        const json = await res.json();
        setReportType(json.reportType);
        setContextInfo({ grade: json.grade || "", schoolYear: json.schoolYear || "" });
        setExistingDefaults(json.reportDefaults || {});
        if (json.logosVersion) {
          getFolderLogos({ userId, folderId: reportFolderId, version: json.logosVersion, getToken })
            .then((fetched) => {
              if (ac.signal.aborted) return;
              setLogos(fetched);
              setLogosVersion(json.logosVersion);
            })
            .catch(() => { /* logos non-critical */ });
        }
      } catch (err) {
        if (isAbortError(err)) return;
        setError(err.message);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    fetchReportFolder();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFolderId]);

  // Called when teacher clicks "Save Defaults" in the form header
  const handleRequestSave = useCallback((values) => {
    const result = {};
    // Include all non-empty values
    for (const [k, v] of Object.entries(values)) {
      if (k === "_completedFields") continue;
      if (String(v ?? "").trim() !== "") result[k] = v;
    }
    // Include fields that were previously saved as defaults but are now cleared
    for (const k of Object.keys(existingDefaults)) {
      if (!(k in result)) result[k] = "";
    }
    setValuesToSave(result);
    setSaveError("");
    setShowConfirm(true);
  }, [existingDefaults]);

  const handleConfirm = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-folders/${reportFolderId}/report-defaults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(valuesToSave),
      });
      if (!res.ok) {
        const body = await res.json();
        setSaveError(body.message || "Failed to save defaults.");
        return;
      }
      setShowConfirm(false);
    } catch {
      setSaveError("Failed to save defaults. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="container my-6 space-y-4">
      <div className="h-7 w-48 bg-muted animate-pulse rounded" />
      <div className="h-4 w-64 bg-muted animate-pulse rounded" />
      <div className="space-y-3 pt-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    </div>
  );
  if (error) return <div className="container my-6 text-destructive">Error: {error}</div>;

  const FormComponent = getPageComponent(reportType);
  if (!FormComponent) {
    return (
      <div className="container my-6 text-muted-foreground">
        Unknown report type: <code>{reportType}</code>
      </div>
    );
  }

  const fieldLabels = REPORT_FIELD_LABELS[reportType] ?? [];
  // Build a label lookup for displaying field names in the confirmation modal
  const labelByKey = Object.fromEntries(fieldLabels.map((f) => [f.key, f.label]));
  // Group the saved values by step for the confirmation list
  const stepOrder = [...new Set(fieldLabels.map((f) => f.step))];
  const byStep = stepOrder.map((step) => ({
    step,
    fields: fieldLabels
      .filter((f) => f.step === step && valuesToSave?.[f.key] !== undefined)
      .map((f) => {
        const clearing = String(valuesToSave[f.key] ?? "").trim() === "";
        return {
          key: f.key,
          label: f.label,
          value: clearing ? (existingDefaults[f.key] ?? "") : valuesToSave[f.key],
          clearing,
        };
      }),
  })).filter((s) => s.fields.length > 0);

  return (
    <>
      <ErrorBoundary>
        <FormComponent
          defaultsGroupId={reportFolderId}
          backUrl={`/report-folders/${reportFolderId}`}
          contextInfo={contextInfo}
          onRequestSave={handleRequestSave}
          logos={logos}
          logosVersion={logosVersion}
        />
      </ErrorBoundary>

      <ReportDefaultsConfirmModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        saving={saving}
        saveError={saveError}
        byStep={byStep}
      />
    </>
  );
};
